import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Checkbox, FormControl } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import Modal from 'react-modal';
import { IBankAccountViewModel } from 'viewModels';
import { cnpj, cpf } from 'cpf-cnpj-validator';
import * as yup from 'yup';
import { useTheme } from 'styled-components';
import { ReactComponent as EditPen } from '../../../assets/icons/editPen.svg';
import { ReactComponent as ExpandArrowIcon } from '../../../assets/icons/iconExpandArrow.svg';
import { MASK_CNPJ, MASK_CPF } from '../../../constants';
import { useAuth } from '../../../context';
import { useBankAccounts } from '../../../context/bankAccounts';
import { WalletController } from '../../../controller/WalletController';
import { BasicButton } from '../../Buttons/BasicButton';
import { Input } from '../../Inputs/Input';
import { SelectControled } from '../../SelectControled';
import { Form, Header } from './style';
import { BankAccountType, labelBankAccountType } from '../../../types/enums';
import { DropDownList } from '../../DropDownList';
import { useAlert } from '../../../hooks/useAlert';

interface ModalEditBankAccountProps {
  isOpen: boolean;
  runningTutorial: boolean;
  currentBankAccount: IBankAccountViewModel;
  onRequestClose: () => void;
  onRequestEditBankAccount: () => void;
}

export function ModalEditBankAccount({
  isOpen,
  currentBankAccount,
  onRequestClose,
  runningTutorial,
  onRequestEditBankAccount,
}: ModalEditBankAccountProps) {
  const alert = useAlert();
  const theme = useTheme();
  const { user } = useAuth();
  const { listBanks, setBankAccounts } = useBankAccounts();
  const [isPreferred, setIsPreferred] = useState<boolean>(false);
  const [defaultValuesBank] = useState({
    bank: currentBankAccount.bank,
    agency: currentBankAccount.agency,
    type: currentBankAccount.type,
    accountNumber: currentBankAccount.accountNumber,
    document: currentBankAccount.document,
    holderName: currentBankAccount.holderName,
  });
  const MenuProps = {
    PaperProps: {
      style: {
        backgroundColor: theme.colors.background_white,
        color: theme.colors.secondary,
        maxHeight: 250,
      },
    },
  };

  const bankSchema = yup.object().shape({
    bank: yup.string().required('Campo obrigatório'),
    holderName: yup.string().required('Campo obrigatório'),
    agency: yup.string().required('Campo obrigatório').min(3, 'Mínimo de 3 dígitos'),
    type: yup.string().required('Campo obrigatório'),
    accountNumber: yup.string().required('Campo obrigatório').min(4, 'Mínimo de 4 dígitos'),
    document: yup
      .string()
      .required('Campo obrigatório')
      .test('cpfCnpjValid', 'CPF ou CNPJ inválido', (value: string) => {
        if (!value) return false;
        const cleanValue = value.replace(/\D/g, '');

        if (cleanValue.length === 11) {
          return cpf.isValid(cleanValue);
        }

        if (cleanValue.length === 14) {
          return cnpj.isValid(cleanValue);
        }

        return false;
      }),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues: defaultValuesBank,
    resolver: yupResolver(bankSchema),
  });
  useEffect(() => {
    setIsPreferred(currentBankAccount.isPreferred);
    setValue('bank', currentBankAccount.bank);
    setValue('agency', currentBankAccount.agency);
    setValue('type', currentBankAccount.type);
    setValue('accountNumber', currentBankAccount.accountNumber);
    setValue('holderName', currentBankAccount.holderName);
    setValue('document', currentBankAccount.document);
  }, [currentBankAccount, setValue]);

  const [bank, agency, type, accountNumber, document, holderName] = useWatch({
    control,
    name: ['bank', 'agency', 'type', 'accountNumber', 'document', 'holderName'],
  });

  const handleUpdateCurrentBank = async () => {
    if (runningTutorial) {
      onRequestClose();
      alert.show({
        message: 'Isso é apenas uma demonstração! A conta bancária nao sera atualizada.',
        type: 'success',
      });
      return;
    }
    const bankAccount: IBankAccountViewModel = {
      id: currentBankAccount.id,
      bank,
      nameBank: listBanks.find((b) => b.code === Number(bank))?.name,
      holderName,
      type,
      agency,
      accountNumber,
      document,
      isPreferred,
    };

    await WalletController.updateBankAccount(bankAccount, user?.id as string)
      .then(() => {
        onRequestEditBankAccount();
        onRequestClose();
        setBankAccounts((prev) => {
          const idxBankAccount = prev.findIndex((b) => b.id === bankAccount.id);
          const newBankAccounts = [...prev];
          newBankAccounts[idxBankAccount] = bankAccount;
          return newBankAccounts;
        });
        alert.show({
          message: 'Conta bancária atualizada com sucesso.',
          type: 'success',
        });
      })
      .catch((error) => {
        if (error?.response?.data === 'ALREADY_REGISTERED') {
          alert.show({
            message:
              'Você está tentando adicionar uma conta bancária já existente. Tente novamente com outra conta bancária.',
            type: 'error',
          });
        } else {
          alert.show({
            message: 'Ocorreu um erro ao atualizar a conta bancária.',
            type: 'error',
          });
        }
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="react-modal-overlay"
      className="react-edit-modal-content">
      <Header>
        <div className="subHeader">
          <EditPen />
          <h1>Editar Conta Bancária</h1>
        </div>
        <div className="close-div">
          <button type="button" onClick={onRequestClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </Header>
      <Form>
        <div className="cnt-form">
          <div className="dropdown">
            <FormControl fullWidth>
              <SelectControled
                name="bank"
                control={control}
                displayEmpty
                classes={{ select: 'select-style' }}
                MenuProps={MenuProps}
                IconComponent={ExpandArrowIcon}
                defaultValue={currentBankAccount.bank}
                placeholderTitle="Banco"
                itens={listBanks.map((b) => ({ id: b.code, value: b.name }))}
              />
            </FormControl>
          </div>
          <div className="dropdown">
            <Controller
              name="type"
              control={control}
              render={({ field: { onChange, value } }) => (
                <DropDownList
                  value={value.toString()}
                  itens={Object.keys(BankAccountType)
                    .filter((typeAccount) => !isNaN(Number(typeAccount)))
                    .map((key) => ({
                      id: key,
                      value: labelBankAccountType[key as unknown as BankAccountType],
                    }))}
                  typeReturn="id"
                  placeholderTitle="Tipo da conta"
                  onChange={(e) => {
                    onChange(e);
                  }}
                  errorText={errors.type?.message}
                />
              )}
            />
          </div>
          <Input
            name="agency"
            placeholder="Agência"
            className="input-form"
            control={control}
            preventNotNumbers
            inputMode="numeric"
            lengthInput={4}
            variant="standard"
            errorText={errors.agency?.message}
          />
          <Input
            name="accountNumber"
            placeholder="Conta"
            className="input-form"
            control={control}
            preventNotNumbers
            inputMode="numeric"
            lengthInput={11}
            variant="standard"
            errorText={errors.accountNumber?.message}
          />
          <Input
            name="holderName"
            placeholder="Nome do Titular"
            className="input-form"
            lengthInput={30}
            control={control}
            variant="standard"
            errorText={errors.holderName?.message}
          />
          <Controller
            name="document"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                name="document"
                placeholder="CPF/CNPJ"
                className="input-form"
                mask={value.length > 14 ? MASK_CNPJ : MASK_CPF}
                lengthInput={18}
                control={control}
                variant="standard"
                errorText={errors.document?.message}
                onChange={onChange}
              />
            )}
          />
          <div className="checkbox-options">
            <Checkbox
              checked={isPreferred}
              onChange={() => {
                setIsPreferred(!isPreferred);
              }}
            />
            Banco preferencial
          </div>
        </div>
        <div className="buttonModal">
          <BasicButton
            variant="outlined"
            size="large"
            onClick={handleSubmit(handleUpdateCurrentBank)}>
            Salvar alterações
          </BasicButton>
        </div>
      </Form>
    </Modal>
  );
}
